import React, { MouseEvent, ReactElement, useState } from 'react';
import { BASE_URL } from '../constants/routes';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Section } from './Section';
import { Container } from './Container';
import { Contact, ServiceOption } from './sections/Contact';
import { References } from './sections/References';
import { consultingReferences } from '../config/referencesConfig';
import { Button } from './buttons/Button';
import { ArrowDownIcon } from './icons/ArrowDownIcon';
import { Colors } from '../constants/colors';
import { Device } from '../constants/device';
import { usePageMeta } from '../helpers/usePageMeta';
import { unprefixedPath } from '../helpers/localizedPath';
import { ProjectSection } from '../model/ProjectSection';

const CONTACT_ANCHOR = 'contact';
const CONSULTANT_PHOTO = '/consulting/marta.webp';

type ListItem = {
  title: string;
  text?: string;
};

type ClinicsGroup = {
  title: string;
  items: ListItem[];
};

export const Consulting = (): ReactElement => {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  const canonical = `${BASE_URL}${unprefixedPath(pathname)}`;
  const title = t('consulting.meta.title');
  const description = t('consulting.meta.description');

  usePageMeta({
    title,
    description,
    canonical,
    ogTitle: title,
    ogDescription: description,
    ogUrl: canonical,
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'ProfessionalService',
      name: t('consulting.hero.title'),
      url: canonical,
      description,
      provider: {
        '@type': 'Organization',
        name: 'Mild Blue',
        url: 'https://www.mild.blue',
      },
    },
  });

  const paragraphsOf = (key: string) => t(key, { returnObjects: true }) as string[];
  const services = t('consulting.labs.services', { returnObjects: true }) as ListItem[];
  const clinicsGroups = t('consulting.clinics.groups', { returnObjects: true }) as ClinicsGroup[];
  const processSteps = t('consulting.process.steps', { returnObjects: true }) as ListItem[];
  const reasons = paragraphsOf('consulting.why.items');

  // The laboratory and the outpatient practice are different audiences, so the
  // overview groups the service areas by who each one is for.
  const audiences: { title: string; areas: { anchor: ProjectSection; label: string }[] }[] = [
    {
      title: t('consulting.services.audienceLaboratories'),
      areas: [
        { anchor: 'consulting-laboratories', label: t('consulting.labs.title') },
        { anchor: 'consulting-audits', label: t('consulting.audits.title') },
      ],
    },
    {
      title: t('consulting.services.audienceClinics'),
      areas: [{ anchor: 'consulting-clinics', label: t('consulting.clinics.title') }],
    },
  ];

  // Which section the visitor came from, so the enquiry says what it is about.
  const [service, setService] = useState('');
  const serviceOptions: ServiceOption[] = audiences.flatMap((audience) => audience.areas.map((area) => ({ value: area.anchor, label: `${audience.title}: ${area.label}` })));

  // Falls back to the browser handling the href when the target is missing.
  const scrollToAnchor = (anchor: string) => (event: MouseEvent) => {
    const target = document.getElementById(anchor);
    if (target) {
      event.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const callToAction = (label: string, area: ProjectSection) => (
    <CtaRow>
      <Button
        as="a"
        href={`#${CONTACT_ANCHOR}`}
        onClick={(event: MouseEvent) => {
          setService(area);
          scrollToAnchor(CONTACT_ANCHOR)(event);
        }}
        maxWidth={'420px'}
      >
        {label}
      </Button>
    </CtaRow>
  );

  const paragraphs = (key: string) => paragraphsOf(key).map((paragraph, index) => <p key={index}>{paragraph}</p>);

  const describedItem = (item: ListItem, key: number) =>
    item.text ? (
      <li key={key}>
        <strong>{item.title}</strong>
        {`: ${item.text}`}
      </li>
    ) : (
      <li key={key}>{item.title}</li>
    );

  return (
    <ConsultingMain>
      <Hero>
        <Container compact>
          <h1>{t('consulting.hero.title')}</h1>
          <DividingLine />
          <HeroSubtitle>{t('consulting.hero.subtitle')}</HeroSubtitle>
          <CtaRow>
            <Button as="a" href={`#${CONTACT_ANCHOR}`} onClick={scrollToAnchor(CONTACT_ANCHOR)} inverse maxWidth={'420px'}>
              {t('consulting.hero.cta')}
            </Button>
          </CtaRow>
        </Container>
      </Hero>

      <Section id={'consulting-intro'} inverse heading={t('consulting.trust.title')}>
        <Container compact>
          <Prose>{paragraphs('consulting.trust.paragraphs')}</Prose>
        </Container>
      </Section>

      <Section id={'consulting-services'} heading={t('consulting.services.title')}>
        <Container compact>
          <Prose>
            <p>{t('consulting.services.intro')}</p>
          </Prose>
          <Audiences>
            {audiences.map((audience) => (
              <Audience key={audience.title}>
                <AudienceTitle>{audience.title}</AudienceTitle>
                <ServiceAreas>
                  {audience.areas.map((area) => (
                    <li key={area.anchor}>
                      <ServiceAreaLink href={`#${area.anchor}`} onClick={scrollToAnchor(area.anchor)}>
                        <span>{area.label}</span>
                        <ArrowDownIcon color={Colors.primary} size={24} strokeWidth={4} />
                      </ServiceAreaLink>
                    </li>
                  ))}
                </ServiceAreas>
              </Audience>
            ))}
          </Audiences>
        </Container>
      </Section>

      <Section id={'consulting-laboratories'} inverse heading={t('consulting.labs.title')}>
        <Container compact>
          <Prose>
            {paragraphs('consulting.labs.paragraphs')}
            <h3>{t('consulting.labs.servicesTitle')}</h3>
            <ItemList>{services.map(describedItem)}</ItemList>
            <Note>{t('consulting.labs.toolNote')}</Note>
          </Prose>
          {callToAction(t('consulting.labs.cta'), 'consulting-laboratories')}
        </Container>
      </Section>

      <Section id={'consulting-process'} heading={t('consulting.process.title')}>
        <Container compact>
          <Steps>
            {processSteps.map((step, key) => (
              <Step key={key}>
                <StepTitle>{step.title}</StepTitle>
                <StepText>{step.text}</StepText>
              </Step>
            ))}
          </Steps>
        </Container>
      </Section>

      <Section id={'consulting-audits'} inverse heading={t('consulting.audits.title')}>
        <Container compact>
          <Prose>{paragraphs('consulting.audits.paragraphs')}</Prose>
          {callToAction(t('consulting.audits.cta'), 'consulting-audits')}
        </Container>
      </Section>

      <Section id={'consulting-clinics'} heading={t('consulting.clinics.title')}>
        <Container compact>
          <Prose>
            <p>{t('consulting.clinics.intro')}</p>
            {clinicsGroups.map((group, groupKey) => (
              <React.Fragment key={groupKey}>
                <h3>{group.title}</h3>
                <ItemList>{group.items.map(describedItem)}</ItemList>
              </React.Fragment>
            ))}
          </Prose>
          {callToAction(t('consulting.clinics.cta'), 'consulting-clinics')}
        </Container>
      </Section>

      <Section id={'consulting-why'} heading={t('consulting.why.title')}>
        <Container compact>
          <Prose>
            <ItemList>
              {reasons.map((reason, key) => (
                <li key={key}>{reason}</li>
              ))}
            </ItemList>
            <TrustNote>{t('consulting.why.trust')}</TrustNote>
          </Prose>
          <Disclaimer>{t('consulting.disclaimer')}</Disclaimer>
        </Container>
      </Section>

      <References
        id={'consulting-references'}
        inverse
        items={consultingReferences}
        translationPrefix={'consulting.references.items'}
        headingKey={'consulting.references.title'}
        fullTextLabels={{ show: t('consulting.references.fullTextShow'), hide: t('consulting.references.fullTextHide') }}
      />

      <Section id={'consulting-consultant'} heading={t('consulting.consultant.title')}>
        <Container compact>
          <Consultant>
            <ConsultantPhoto src={CONSULTANT_PHOTO} alt={t('consulting.consultant.name')} width={640} height={640} />
            <ConsultantProfile>
              <ConsultantName>{t('consulting.consultant.name')}</ConsultantName>
              <ConsultantPosition>{t('consulting.consultant.position')}</ConsultantPosition>
              <ConsultantBio>
                <p>{t('consulting.consultant.summary')}</p>
                {paragraphs('consulting.consultant.paragraphs')}
              </ConsultantBio>
              <ConsultantContact>
                <a href={`mailto:${t('consulting.contact.emailValue')}`}>{t('consulting.contact.emailValue')}</a>
                <a href={`tel:${t('consulting.contact.phoneValue').replace(/\s/g, '')}`}>{t('consulting.contact.phoneValue')}</a>
              </ConsultantContact>
            </ConsultantProfile>
          </Consultant>
        </Container>
      </Section>

      <Section id={'consulting-contact'} inverse heading={t('consulting.contact.title')}>
        <Container compact>
          <Prose>
            <p>{t('consulting.contact.intro')}</p>
          </Prose>
          <ContactDetails>
            <div>
              <dt>{t('consulting.contact.personLabel')}</dt>
              <dd>{t('consulting.contact.personValue')}</dd>
            </div>
            <div>
              <dt>{t('consulting.contact.emailLabel')}</dt>
              <dd>
                <a href={`mailto:${t('consulting.contact.emailValue')}`}>{t('consulting.contact.emailValue')}</a>
              </dd>
            </div>
            <div>
              <dt>{t('consulting.contact.phoneLabel')}</dt>
              <dd>
                <a href={`tel:${t('consulting.contact.phoneValue').replace(/\s/g, '')}`}>{t('consulting.contact.phoneValue')}</a>
              </dd>
            </div>
            <div>
              <dt>{t('consulting.contact.webLabel')}</dt>
              <dd>
                <a href={BASE_URL}>{t('consulting.contact.webValue')}</a>
              </dd>
            </div>
          </ContactDetails>
          {callToAction(t('consulting.contact.cta'), 'consulting-contact')}
        </Container>
      </Section>

      <Contact
        subtitleKey={'consulting.contactForm.subtitle'}
        pageName={'slp.blue – poradenství'}
        serviceOptions={serviceOptions}
        serviceLabel={t('consulting.contactForm.serviceLabel')}
        servicePlaceholder={t('consulting.contactForm.servicePlaceholder')}
        service={service}
        onServiceChange={setService}
      />
    </ConsultingMain>
  );
};

const ConsultingMain = styled.main`
  a:focus-visible,
  button:focus-visible {
    outline: 3px solid ${Colors.primaryDark};
    outline-offset: 3px;
  }
`;

const Hero = styled.div`
  background: ${Colors.primary};
  color: ${Colors.white};
  padding: 80px 0 90px;

  ${Device.tablet} {
    padding: 40px 0 50px;
  }

  ${Container} {
    align-items: flex-start;
  }

  h1 {
    font-size: 42px;
    font-weight: 900;
    margin: 0 0 10px;
    max-width: 900px;

    ${Device.tablet} {
      font-size: 28px;
    }
  }
`;

const DividingLine = styled.div`
  background: ${Colors.primaryLight};
  height: 5px;
  width: 100px;
  margin-bottom: 25px;
`;

const HeroSubtitle = styled.p`
  font-size: 22px;
  font-weight: 500;
  line-height: 1.6;
  max-width: 780px;
  margin: 0;

  ${Device.tablet} {
    font-size: 18px;
  }
`;

const CtaRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 35px;

  a {
    text-decoration: none;
  }
`;

const Prose = styled.div`
  /* Section centres its paragraphs; long form copy and lists read better left aligned. */
  p {
    text-align: left !important;
  }

  h3 {
    font-size: 22px;
    margin: 40px 0 10px;

    ${Device.tablet} {
      font-size: 19px;
    }
  }
`;

const Audiences = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 30px;
  margin-top: 30px;

  ${Device.tablet} {
    grid-template-columns: 1fr;
  }
`;

const Audience = styled.div`
  display: flex;
  flex-direction: column;
`;

const AudienceTitle = styled.h3`
  margin: 0 0 15px;
  font-size: 18px;
  text-align: center;
`;

const ServiceAreas = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 15px;
  list-style: none;
  margin: 0;
  padding: 0;
`;

const ServiceAreaLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;
  height: 100%;
  padding: 20px 25px;
  border-radius: 20px;
  background: ${Colors.white};
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  color: ${Colors.primary};
  font-weight: 700;
  text-decoration: none;
  transition:
    box-shadow 0.2s ease,
    transform 0.2s ease;

  &:hover {
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.18);
    transform: translateY(-2px);
  }

  svg {
    flex: none;
  }
`;

const ItemList = styled.ul`
  line-height: 2;
  padding-left: 20px;
  margin: 0;

  li {
    margin-bottom: 8px;
  }
`;

const Note = styled.p`
  margin-top: 30px;
  color: ${Colors.darkGrey};
`;

const TrustNote = styled.p`
  margin-top: 30px;
`;

const Steps = styled.ol`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  list-style: none;
  counter-reset: step;
  margin: 0;
  padding: 0;

  ${Device.laptop} {
    grid-template-columns: repeat(2, 1fr);
  }

  ${Device.tablet} {
    grid-template-columns: 1fr;
  }
`;

const Step = styled.li`
  counter-increment: step;
  background: ${Colors.white};
  border-radius: 20px;
  padding: 25px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);

  &:before {
    content: counter(step);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: ${Colors.primary};
    color: ${Colors.white};
    font-weight: 700;
    margin-bottom: 15px;
  }
`;

const StepTitle = styled.h3`
  font-size: 18px;
  margin: 0 0 8px;
`;

const StepText = styled.p`
  margin: 0;
  /* Overrides the centred, double spaced paragraph styling of Section. */
  text-align: left !important;
  line-height: 1.7 !important;
`;

const Disclaimer = styled.p`
  margin-top: 40px;
  font-size: 13px;
  color: ${Colors.darkGrey};
  /* Overrides the centred, double spaced paragraph styling of Section. */
  text-align: left !important;
  line-height: 1.8 !important;
`;

const Consultant = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 30px;
  margin-bottom: 30px;

  ${Device.tablet} {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`;

const ConsultantPhoto = styled.img`
  width: 180px;
  height: 180px;
  flex: none;
  border-radius: 50%;
  object-fit: cover;

  ${Device.tablet} {
    width: 150px;
    height: 150px;
  }
`;

const ConsultantProfile = styled.div`
  display: flex;
  flex-direction: column;
`;

const ConsultantName = styled.h3`
  margin: 0;
  font-size: 22px;
`;

const ConsultantPosition = styled.p`
  margin: 5px 0 0;
  color: ${Colors.darkGrey};
  text-align: left !important;

  ${Device.tablet} {
    text-align: center !important;
  }
`;

const ConsultantBio = styled.div`
  /* Stays left aligned on narrow screens; centring this many lines reads badly. */
  p {
    margin: 15px 0 0;
    text-align: left !important;
  }
`;

const ConsultantContact = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px 30px;
  margin-top: 15px;

  a {
    color: ${Colors.primary};
    font-weight: 700;
  }

  ${Device.tablet} {
    justify-content: center;
  }
`;

const ContactDetails = styled.dl`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px 40px;
  /* Auto side margins would stop the definition list from stretching inside the
     flex column of Container, so it is left to fill the container width. */
  margin: 30px 0 0;

  ${Device.tablet} {
    grid-template-columns: 1fr;
  }

  dt {
    font-weight: 700;
  }

  dd {
    margin: 5px 0 0;
  }

  a {
    color: ${Colors.primary};
    font-weight: 700;
  }
`;
